/**
 * The persisted dashboard document: pages of tiles on a 12-column grid,
 * dashboard-level filters, data preparation and a theme.
 * `normalizeDashboardDocument` is the single load-time sanitiser; every other
 * module can trust the shape it returns.
 */

import {
  STATISTIC_TYPE,
  TABLE_OPERATION_TYPE,
} from "types/ReportingDashboard/elements.types"
import {
  SignificanceSettings,
  normalizeSignificanceSettings,
} from "types/significance.types"
import {
  TableStyleChoice,
  normalizeTableStyleChoice,
} from "types/tableStyle.types"

export const SCHEMA_VERSION = 2 as const
/** The table look of a dashboard without a table style of its own. */
export const DASHBOARD_TABLE_STYLE: TableStyleChoice = { preset: "light" }
/** Placeholder in a script source, replaced by the tile's universe at run time. */
export const UNIVERSE_TOKEN = "{{universe}}"
export const GRID_COLUMNS = 12
export const MIN_TILE_W = 2
export const MIN_TILE_H = 2

export type TileType =
  | "kpi"
  | "bar"
  | "line"
  | "pie"
  | "table"
  | "scorecard"
  | "text"
  | "image"
export const TILE_TYPES: readonly TileType[] = [
  "kpi",
  "bar",
  "line",
  "pie",
  "table",
  "scorecard",
  "text",
  "image",
]

export const DEFAULT_TILE_SIZES: Record<TileType, { w: number; h: number }> = {
  kpi: { w: 3, h: 3 },
  bar: { w: 6, h: 6 },
  line: { w: 6, h: 6 },
  pie: { w: 4, h: 6 },
  table: { w: 6, h: 8 },
  scorecard: { w: 3, h: 3 },
  text: { w: 4, h: 3 },
  image: { w: 4, h: 4 },
}

export interface TileLayout {
  x: number
  y: number
  w: number
  h: number
}

export interface FilterDef {
  id: string
  label: string
  variable: string
  control: "multi" | "single"
  defaultSelection: string[]
}

export interface BuilderSource {
  kind: "builder"
  operation: TABLE_OPERATION_TYPE
  rows: string[]
  columns: string[]
  statistics: STATISTIC_TYPE[]
  significance?: SignificanceSettings
  /**
   * The tile's base: each inner array is one group of codebook paths joined
   * with OR, and the groups are joined with AND. Empty groups are dropped.
   */
  conditions: string[][]
  removed: { rows: string[]; columns: string[] }
}
export interface ScriptSource {
  kind: "script"
  script: string
  outputIndex: number
  /**
   * The tile's base, as on a builder source (OR within a group, AND across
   * groups): "Switch to script" copies the builder's groups. It joins the
   * filters wherever the script has `{{universe}}`.
   */
  conditions: string[][]
  /**
   * A single base path, as older files and hand-typed files carry it. The
   * sanitiser folds it into `conditions` and drops it, so nothing reads it.
   */
  universe?: string
}
export type TileSource = BuilderSource | ScriptSource

export interface TileControlsConfig {
  statistic: boolean
  breakdown: boolean
  categories: boolean
}
export interface CrossFilterConfig {
  emits: boolean
  receives: boolean
}

export type MeasureKind =
  | "net"
  | "difference"
  | "mean"
  | "rolling"
  | "delta"
  | "rank"
export type MeasureFormat = "percent" | "points" | "number"
interface MeasureBase {
  id: string
  label: string
  kind: MeasureKind
  format?: MeasureFormat
  decimals?: number
}
/** The sum of the member rows (row leaf ids). */
export interface NetMeasure extends MeasureBase {
  kind: "net"
  members: string[]
}
/** `plus` minus `minus`, each a row leaf id or a measure id. */
export interface DifferenceMeasure extends MeasureBase {
  kind: "difference"
  plus: string
  minus: string
}
/** The mean of the scored rows, keyed by row leaf id. */
export interface MeanMeasure extends MeasureBase {
  kind: "mean"
  scaleValues: Record<string, number>
}
/** The base-weighted mean of `source` over the last `periods` columns. */
export interface RollingMeasure extends MeasureBase {
  kind: "rolling"
  source: string
  periods: number
  requireFullWindow: boolean
}
/** `source` in a column minus `source` in the column before it. */
export interface DeltaMeasure extends MeasureBase {
  kind: "delta"
  source: string
}
/** The rank of `source` among the columns, 1 = best in `direction`. */
export interface RankMeasure extends MeasureBase {
  kind: "rank"
  source: string
  direction: "desc" | "asc"
}
export type Measure =
  | NetMeasure
  | DifferenceMeasure
  | MeanMeasure
  | RollingMeasure
  | DeltaMeasure
  | RankMeasure
export const MEASURE_KINDS: readonly MeasureKind[] = [
  "net",
  "difference",
  "mean",
  "rolling",
  "delta",
  "rank",
]

/** Which rows a data tile shows: the categories, its measures, or both. */
export type RowsDisplay = "categories" | "measures" | "both"
/** Display-only order, hiding and renaming of a tile's rows, by row id. */
export interface CategoryDisplay {
  order?: string[]
  hidden: string[]
  labels: Record<string, string>
}
export interface ChartOptions {
  dataLabels: "off" | "on"
  target?: { value: number; label: string }
  sort: "none" | "desc" | "asc"
  axisMin?: number
  axisMax?: number
  legend: "bottom" | "right" | "none"
  normalize: boolean
}
/** Small multiples: one panel per category of `variable`. */
export interface RepeatBy {
  variable: string
  columns: 2 | 3 | 4
}
export const MAX_PANELS = 12
/** The dashboard's data preparation: saved pipelines, in order, and a weight. */
export interface DashboardData {
  pipelines: string[]
  weight: string | null
}

export const DEFAULT_CHART_OPTIONS: ChartOptions = {
  dataLabels: "off",
  sort: "none",
  legend: "bottom",
  normalize: false,
}
export const EMPTY_CATEGORY_DISPLAY: CategoryDisplay = { hidden: [], labels: {} }

interface TileBase {
  id: string
  title?: string
  layout: TileLayout
}
interface DataTileBase extends TileBase {
  source: TileSource
  controls: TileControlsConfig
  crossFilter: CrossFilterConfig
  measures: Measure[]
  display: { rows: RowsDisplay }
  categories: CategoryDisplay
  chart: ChartOptions
  repeatBy?: RepeatBy
  unweighted: boolean
}

export interface KpiTile extends DataTileBase {
  type: "kpi"
  visual: {
    row: string | null
    column: string | null
    statistic: STATISTIC_TYPE
    showBase: boolean
    decimals?: number
  }
}
export interface BarTile extends DataTileBase {
  type: "bar"
  visual: {
    orientation: "vertical" | "horizontal"
    mode: "grouped" | "stacked"
    statistic: STATISTIC_TYPE
    showTotals: boolean
    seriesAxis: "across" | "down"
    /**
     * Column categories left out of the chart (Hide columns), used while
     * the series come from the rows, so the columns are the categories.
     * Rows are hidden through the category display.
     */
    hiddenCategories: string[]
  }
}
export interface LineTile extends DataTileBase {
  type: "line"
  visual: {
    statistic: STATISTIC_TYPE
    waveVariable: string
    hiddenSeries: string[]
    showPoints: boolean
  }
}
export interface PieTile extends DataTileBase {
  type: "pie"
  visual: {
    statistic: STATISTIC_TYPE
    donut: boolean
    legend: "right" | "bottom" | "none"
  }
}
export interface TableTile extends DataTileBase {
  type: "table"
  visual: {
    tableStyle?: TableStyleChoice
    transposed: boolean
    fontScale?: number
  }
}
export interface ScorecardTile extends DataTileBase {
  type: "scorecard"
  visual: {
    /** A row leaf id or a measure id. */
    source: string
    /** null = the last column, or Totals without a column variable. */
    column: string | null
    compare: "previous" | "target" | "none"
    target?: number
    goodDirection: "up" | "down"
    showRank: boolean
    decimals?: number
  }
}
export interface TextTile extends TileBase {
  type: "text"
  markdown: string
}
export interface ImageTile extends TileBase {
  type: "image"
  src: string | null
  fit: "contain" | "cover"
}

export type DataTile =
  | KpiTile
  | BarTile
  | LineTile
  | PieTile
  | TableTile
  | ScorecardTile
export type Tile = DataTile | TextTile | ImageTile

export interface Page {
  id: string
  title: string
  tiles: Tile[]
}

export interface DashboardDocument {
  schemaVersion: typeof SCHEMA_VERSION
  title: string
  description?: string
  createdAt: number
  updatedAt: number
  prepScript: string
  data: DashboardData
  theme: {
    tableStyle?: TableStyleChoice
    chartPalette?: string
    /** Category or measure label to a `#rrggbb` colour, dashboard-wide. */
    categoryColours: Record<string, string>
  }
  filters: FilterDef[]
  pages: Page[]
}

export const newId = (
  prefix: "page" | "tile" | "filter" | "measure"
): string =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`

export const isDataTile = (tile: Tile): tile is DataTile =>
  tile.type !== "text" && tile.type !== "image"

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)
const str = (v: unknown, fallback = ""): string =>
  typeof v === "string" ? v : fallback
const num = (v: unknown, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback
const bool = (v: unknown, fallback: boolean): boolean =>
  typeof v === "boolean" ? v : fallback
const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : []
const STAT_VALUES = new Set<string>(Object.values(STATISTIC_TYPE))
const stat = (v: unknown, fallback: STATISTIC_TYPE): STATISTIC_TYPE =>
  typeof v === "string" && STAT_VALUES.has(v) ? (v as STATISTIC_TYPE) : fallback
const statArray = (
  v: unknown,
  fallback: STATISTIC_TYPE[]
): STATISTIC_TYPE[] => {
  const kept = strArray(v).filter((s) => STAT_VALUES.has(s)) as STATISTIC_TYPE[]
  return kept.length > 0 ? kept : fallback
}
const oneOf = <T extends string>(
  v: unknown,
  options: readonly T[],
  fallback: T
): T =>
  typeof v === "string" && (options as readonly string[]).includes(v)
    ? (v as T)
    : fallback

/**
 * A layout snapped to whole cells and kept on the grid: at least
 * `MIN_TILE_W` x `MIN_TILE_H`, no wider than the grid, `x` pulled in so the
 * tile does not overflow the right edge, no negative `y`.
 */
export const clampLayout = (
  layout: Partial<TileLayout>,
  columns = GRID_COLUMNS
): TileLayout => {
  const w = Math.min(
    columns,
    Math.max(MIN_TILE_W, Math.round(num(layout.w, MIN_TILE_W)))
  )
  const h = Math.max(MIN_TILE_H, Math.round(num(layout.h, MIN_TILE_H)))
  const x = Math.min(columns - w, Math.max(0, Math.round(num(layout.x, 0))))
  const y = Math.max(0, Math.round(num(layout.y, 0)))
  return { x, y, w, h }
}

const defaultSource = (): BuilderSource => ({
  kind: "builder",
  operation: TABLE_OPERATION_TYPE.XTAB,
  rows: [],
  columns: [],
  statistics: [STATISTIC_TYPE.COL_PERCENT],
  conditions: [],
  removed: { rows: [], columns: [] },
})

/** Condition groups of trimmed, non-blank paths; empty groups are dropped. */
const normalizeConditions = (v: unknown): string[][] =>
  (Array.isArray(v) ? v : [])
    .map((group) =>
      strArray(group)
        .map((p) => p.trim())
        .filter(Boolean)
    )
    .filter((group) => group.length > 0)

/** A non-blank path, trimmed; undefined otherwise. */
const universePath = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim() : undefined

const normalizeSource = (raw: unknown): TileSource => {
  if (!isRecord(raw)) return defaultSource()
  if (raw.kind === "script") {
    // The base is `conditions` when the file has them; a single `universe`
    // path (older and hand-typed files) becomes a group of one. A file with
    // both keeps its `conditions`, even an empty list, and drops the
    // `universe`: the app never writes both, so only a hand-edited file
    // has them.
    const universe = universePath(raw.universe)
    return {
      kind: "script",
      script: str(raw.script),
      outputIndex: Math.max(0, Math.round(num(raw.outputIndex, 0))),
      conditions: Array.isArray(raw.conditions)
        ? normalizeConditions(raw.conditions)
        : universe
          ? [[universe]]
          : [],
    }
  }
  const removed = isRecord(raw.removed) ? raw.removed : {}
  const source: BuilderSource = {
    kind: "builder",
    operation: oneOf(
      raw.operation,
      [TABLE_OPERATION_TYPE.XTAB, TABLE_OPERATION_TYPE.DTAB],
      TABLE_OPERATION_TYPE.XTAB
    ),
    rows: strArray(raw.rows),
    columns: strArray(raw.columns),
    statistics: statArray(raw.statistics, [STATISTIC_TYPE.COL_PERCENT]),
    conditions: normalizeConditions(raw.conditions),
    removed: { rows: strArray(removed.rows), columns: strArray(removed.columns) },
  }
  const sig = normalizeSignificanceSettings(raw.significance)
  if (sig) source.significance = sig
  return source
}

/** A finite number; undefined otherwise. */
const finite = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined

/** A finite number rounded and held within min..max; undefined otherwise. */
const intInRange = (v: unknown, min: number, max: number): number | undefined => {
  const n = finite(v)
  return n === undefined ? undefined : Math.min(max, Math.max(min, Math.round(n)))
}

/** The entries of `v` whose value is a finite number. */
const numberRecord = (v: unknown): Record<string, number> =>
  isRecord(v)
    ? Object.fromEntries(
        Object.entries(v).filter(
          (e): e is [string, number] => finite(e[1]) !== undefined
        )
      )
    : {}

/** The entries of `v` whose value is a string. */
const stringRecord = (v: unknown): Record<string, string> =>
  isRecord(v)
    ? Object.fromEntries(
        Object.entries(v).filter(
          (e): e is [string, string] => typeof e[1] === "string"
        )
      )
    : {}

const MEASURE_FORMATS: readonly MeasureFormat[] = ["percent", "points", "number"]

/**
 * A measure checked against its kind, or null (with a console warning) for
 * an unknown kind. A missing id gets a fresh one. References to rows and
 * other measures are kept as written: the measures engine reports the ones
 * it cannot find.
 */
const normalizeMeasure = (raw: unknown): Measure | null => {
  if (!isRecord(raw)) return null
  const kind = raw.kind
  if (typeof kind !== "string" || !(MEASURE_KINDS as string[]).includes(kind)) {
    console.warn("[dashboard] dropped unknown measure kind", kind)
    return null
  }
  const decimals = intInRange(raw.decimals, 0, 4)
  const base = {
    id: str(raw.id) || newId("measure"),
    label: str(raw.label),
    ...(typeof raw.format === "string" &&
    (MEASURE_FORMATS as string[]).includes(raw.format)
      ? { format: raw.format as MeasureFormat }
      : {}),
    ...(decimals !== undefined ? { decimals } : {}),
  }
  switch (kind as MeasureKind) {
    case "net":
      return { ...base, kind: "net", members: strArray(raw.members) }
    case "difference":
      return {
        ...base,
        kind: "difference",
        plus: str(raw.plus),
        minus: str(raw.minus),
      }
    case "mean":
      return {
        ...base,
        kind: "mean",
        scaleValues: numberRecord(raw.scaleValues),
      }
    case "rolling":
      return {
        ...base,
        kind: "rolling",
        source: str(raw.source),
        periods: intInRange(raw.periods, 2, 12) ?? 2,
        requireFullWindow: bool(raw.requireFullWindow, false),
      }
    case "delta":
      return { ...base, kind: "delta", source: str(raw.source) }
    case "rank":
      return {
        ...base,
        kind: "rank",
        source: str(raw.source),
        direction: oneOf(raw.direction, ["desc", "asc"], "desc"),
      }
  }
}

/**
 * Chart options with bad or missing fields defaulted. `dataLabels` is the
 * default for data labels: off, except on a pie, which shows its values.
 * An axis pair whose end is not above its start is dropped whole, as the
 * Display tab never writes one.
 */
const normalizeChart = (
  raw: unknown,
  dataLabels: ChartOptions["dataLabels"] = DEFAULT_CHART_OPTIONS.dataLabels
): ChartOptions => {
  const rec = isRecord(raw) ? raw : {}
  const target = isRecord(rec.target) ? rec.target : null
  const targetValue = target ? finite(target.value) : undefined
  const min = finite(rec.axisMin)
  const max = finite(rec.axisMax)
  const inverted = min !== undefined && max !== undefined && min >= max
  const axisMin = inverted ? undefined : min
  const axisMax = inverted ? undefined : max
  return {
    dataLabels: oneOf(rec.dataLabels, ["off", "on"], dataLabels),
    ...(target && targetValue !== undefined
      ? { target: { value: targetValue, label: str(target.label) } }
      : {}),
    sort: oneOf(rec.sort, ["none", "desc", "asc"], DEFAULT_CHART_OPTIONS.sort),
    ...(axisMin !== undefined ? { axisMin } : {}),
    ...(axisMax !== undefined ? { axisMax } : {}),
    legend: oneOf(
      rec.legend,
      ["bottom", "right", "none"],
      DEFAULT_CHART_OPTIONS.legend
    ),
    normalize: bool(rec.normalize, DEFAULT_CHART_OPTIONS.normalize),
  }
}

const normalizeCategories = (raw: unknown): CategoryDisplay => {
  const rec = isRecord(raw) ? raw : {}
  return {
    ...(Array.isArray(rec.order) ? { order: strArray(rec.order) } : {}),
    hidden: strArray(rec.hidden),
    labels: stringRecord(rec.labels),
  }
}

const REPEAT_COLUMNS: readonly RepeatBy["columns"][] = [2, 3, 4]

/** Small multiples over a non-blank variable, 3 columns unless 2 or 4. */
const normalizeRepeatBy = (raw: unknown): RepeatBy | undefined => {
  if (!isRecord(raw)) return undefined
  const variable = universePath(raw.variable)
  if (!variable) return undefined
  return {
    variable,
    columns: REPEAT_COLUMNS.find((c) => c === raw.columns) ?? 3,
  }
}

const ROWS_DISPLAYS: readonly RowsDisplay[] = ["categories", "measures", "both"]

const dataBase = (
  raw: Record<string, unknown>,
  id: string,
  layout: TileLayout
) => {
  const controls = isRecord(raw.controls) ? raw.controls : {}
  const cf = isRecord(raw.crossFilter) ? raw.crossFilter : {}
  const display = isRecord(raw.display) ? raw.display : {}
  const measures = (Array.isArray(raw.measures) ? raw.measures : [])
    .map(normalizeMeasure)
    .filter((m): m is Measure => m !== null)
  const repeatBy = normalizeRepeatBy(raw.repeatBy)
  return {
    id,
    layout,
    title: typeof raw.title === "string" ? raw.title : undefined,
    source: normalizeSource(raw.source),
    controls: {
      statistic: bool(controls.statistic, false),
      breakdown: bool(controls.breakdown, false),
      categories: bool(controls.categories, false),
    },
    crossFilter: { emits: bool(cf.emits, true), receives: bool(cf.receives, true) },
    measures,
    display: {
      rows: oneOf(
        display.rows,
        ROWS_DISPLAYS,
        measures.length > 0 ? "measures" : "categories"
      ),
    },
    categories: normalizeCategories(raw.categories),
    chart: normalizeChart(raw.chart),
    ...(repeatBy ? { repeatBy } : {}),
    unweighted: bool(raw.unweighted, false),
  }
}

/**
 * A pie tile's category display with the ids of its old "Hide categories"
 * choice (`visual.hiddenCategories`, which is dropped) added to its hidden
 * rows, each once: the category editor's eye is the one way to hide a
 * slice. (A bar keeps the field: it hides columns there.)
 */
const foldHiddenCategories = (
  categories: CategoryDisplay,
  visual: Record<string, unknown>
): CategoryDisplay => {
  const folded = [
    ...new Set(strArray(visual.hiddenCategories)),
  ].filter((id) => !categories.hidden.includes(id))
  return folded.length === 0
    ? categories
    : { ...categories, hidden: [...categories.hidden, ...folded] }
}

const normalizeTile = (raw: unknown): Tile | null => {
  if (
    !isRecord(raw) ||
    typeof raw.type !== "string" ||
    !(TILE_TYPES as string[]).includes(raw.type)
  ) {
    return null
  }
  const type = raw.type as TileType
  const id = str(raw.id) || newId("tile")
  const size = DEFAULT_TILE_SIZES[type]
  const layout = clampLayout({
    ...size,
    ...(isRecord(raw.layout) ? raw.layout : {}),
  })
  const visual = isRecord(raw.visual) ? raw.visual : {}
  switch (type) {
    case "text":
      return {
        id,
        type,
        layout,
        title: typeof raw.title === "string" ? raw.title : undefined,
        markdown: str(raw.markdown),
      }
    case "image":
      return {
        id,
        type,
        layout,
        title: typeof raw.title === "string" ? raw.title : undefined,
        // Only an inline image: never a URL the page would fetch.
        src:
          typeof raw.src === "string" && raw.src.startsWith("data:image/")
            ? raw.src
            : null,
        fit: oneOf(raw.fit, ["contain", "cover"], "contain"),
      }
    case "kpi": {
      // Held to 0..4 places, as the Display tab offers: toFixed throws out
      // of range, and a thrown render blanks the dashboard.
      const decimals = intInRange(visual.decimals, 0, 4)
      return {
        ...dataBase(raw, id, layout),
        type,
        visual: {
          row: typeof visual.row === "string" ? visual.row : null,
          column: typeof visual.column === "string" ? visual.column : null,
          statistic: stat(visual.statistic, STATISTIC_TYPE.COL_PERCENT),
          showBase: bool(visual.showBase, true),
          ...(decimals !== undefined ? { decimals } : {}),
        },
      }
    }
    case "bar":
      return {
        ...dataBase(raw, id, layout),
        type,
        visual: {
          orientation: oneOf(
            visual.orientation,
            ["vertical", "horizontal"],
            "vertical"
          ),
          mode: oneOf(visual.mode, ["grouped", "stacked"], "grouped"),
          statistic: stat(visual.statistic, STATISTIC_TYPE.COL_PERCENT),
          showTotals: bool(visual.showTotals, false),
          seriesAxis: oneOf(visual.seriesAxis, ["across", "down"], "across"),
          hiddenCategories: [...new Set(strArray(visual.hiddenCategories))],
        },
      }
    case "line":
      return {
        ...dataBase(raw, id, layout),
        type,
        visual: {
          statistic: stat(visual.statistic, STATISTIC_TYPE.COL_PERCENT),
          waveVariable: str(visual.waveVariable),
          hiddenSeries: strArray(visual.hiddenSeries),
          showPoints: bool(visual.showPoints, true),
        },
      }
    case "pie": {
      const base = dataBase(raw, id, layout)
      return {
        ...base,
        categories: foldHiddenCategories(base.categories, visual),
        chart: normalizeChart(raw.chart, "on"),
        type,
        visual: {
          statistic: stat(visual.statistic, STATISTIC_TYPE.COL_PERCENT),
          donut: bool(visual.donut, false),
          legend: oneOf(visual.legend, ["right", "bottom", "none"], "right"),
        },
      }
    }
    case "table": {
      const style = normalizeTableStyleChoice(visual.tableStyle)
      return {
        ...dataBase(raw, id, layout),
        type,
        visual: {
          ...(style ? { tableStyle: style } : {}),
          transposed: bool(visual.transposed, false),
          ...(typeof visual.fontScale === "number"
            ? { fontScale: visual.fontScale }
            : {}),
        },
      }
    }
    case "scorecard": {
      const target = finite(visual.target)
      // Held to 0..4 places, as on a KPI.
      const decimals = intInRange(visual.decimals, 0, 4)
      return {
        ...dataBase(raw, id, layout),
        type,
        visual: {
          source: str(visual.source),
          column: typeof visual.column === "string" ? visual.column : null,
          compare: oneOf(
            visual.compare,
            ["previous", "target", "none"],
            "previous"
          ),
          ...(target !== undefined ? { target } : {}),
          goodDirection: oneOf(visual.goodDirection, ["up", "down"], "up"),
          showRank: bool(visual.showRank, false),
          ...(decimals !== undefined ? { decimals } : {}),
        },
      }
    }
  }
}

/** A new tile of `type` at `layout`, every other field defaulted. */
export const createTile = (type: TileType, layout: TileLayout): Tile =>
  normalizeTile({ id: newId("tile"), type, layout }) as Tile

const normalizePage = (raw: unknown, index: number): Page => {
  const rec = isRecord(raw) ? raw : {}
  const tiles = Array.isArray(rec.tiles) ? rec.tiles : []
  return {
    id: str(rec.id) || newId("page"),
    title: str(rec.title) || `Page ${index + 1}`,
    tiles: tiles.map(normalizeTile).filter((t): t is Tile => t !== null),
  }
}

const normalizeFilter = (raw: unknown): FilterDef | null => {
  if (
    !isRecord(raw) ||
    typeof raw.variable !== "string" ||
    !raw.variable.trim()
  ) {
    return null
  }
  const variable = raw.variable.trim()
  return {
    id: str(raw.id) || newId("filter"),
    label: str(raw.label) || variable.split("//").pop() || variable,
    variable,
    control: oneOf(raw.control, ["multi", "single"], "multi"),
    defaultSelection: strArray(raw.defaultSelection),
  }
}

/**
 * A new document: one empty page titled "Page 1", no filters, no pipelines
 * or weight, no theme choices.
 */
export const createEmptyDocument = (title: string): DashboardDocument => {
  const now = Date.now()
  return {
    schemaVersion: SCHEMA_VERSION,
    title,
    createdAt: now,
    updatedAt: now,
    prepScript: "",
    data: { pipelines: [], weight: null },
    theme: { categoryColours: {} },
    filters: [],
    pages: [{ id: newId("page"), title: "Page 1", tiles: [] }],
  }
}

export type NormalizeResult =
  | { ok: true; document: DashboardDocument }
  | { ok: false; reason: "old-format" | "invalid" }

const HEX_COLOUR = /^#[0-9a-f]{6}$/i

/**
 * Turns a loaded body (a JSON string or an already parsed value) into a
 * valid document. Refuses version 1 dashboards and old Reporting files as
 * "old-format" (there is no upgrade path) and anything else that is not a
 * version 2 document as "invalid". Within a version 2 document, bad fields
 * fall back to defaults, unknown tile types, unknown measure kinds and
 * filters without a variable are dropped, and a document with no pages gets
 * one. Never throws.
 */
export const normalizeDashboardDocument = (raw: unknown): NormalizeResult => {
  let value = raw
  if (typeof value === "string") {
    if (!value.trim()) return { ok: false, reason: "invalid" }
    try {
      value = JSON.parse(value)
    } catch {
      return { ok: false, reason: "invalid" }
    }
  }
  if (!isRecord(value)) return { ok: false, reason: "invalid" }
  if (value.schemaVersion !== SCHEMA_VERSION) {
    // Version 1 dashboards are not upgraded. Old Reporting files have
    // `dashboard` + `excelReport` and no schemaVersion.
    return {
      ok: false,
      reason:
        value.schemaVersion === 1 || "dashboard" in value || "excelReport" in value
          ? "old-format"
          : "invalid",
    }
  }
  const data = isRecord(value.data) ? value.data : {}
  const theme = isRecord(value.theme) ? value.theme : {}
  const tableStyle = normalizeTableStyleChoice(theme.tableStyle)
  const categoryColours = Object.fromEntries(
    Object.entries(stringRecord(theme.categoryColours)).filter(([, c]) =>
      HEX_COLOUR.test(c)
    )
  )
  const pagesRaw =
    Array.isArray(value.pages) && value.pages.length > 0 ? value.pages : [{}]
  const now = Date.now()
  return {
    ok: true,
    document: {
      schemaVersion: SCHEMA_VERSION,
      title: str(value.title) || "Untitled dashboard",
      ...(typeof value.description === "string"
        ? { description: value.description }
        : {}),
      createdAt: num(value.createdAt, now),
      updatedAt: num(value.updatedAt, now),
      prepScript: str(value.prepScript),
      data: {
        // Each pipeline once, where it first appears: a repeat would run it twice.
        pipelines: [...new Set(strArray(data.pipelines))],
        weight:
          typeof data.weight === "string" && data.weight.trim()
            ? data.weight.trim()
            : null,
      },
      theme: {
        ...(tableStyle ? { tableStyle } : {}),
        ...(typeof theme.chartPalette === "string"
          ? { chartPalette: theme.chartPalette }
          : {}),
        categoryColours,
      },
      filters: (Array.isArray(value.filters) ? value.filters : [])
        .map(normalizeFilter)
        .filter((f): f is FilterDef => f !== null),
      pages: pagesRaw.map(normalizePage),
    },
  }
}
